import React, { memo, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Box, createStyles, makeStyles, Table, Typography } from 'transfix-ui/core'
import { IAs2ConnectionConfig, IConnectionConfigTypes, IConnectionTypes } from 'types/graphqlTypes'
import { Dialog } from 'components/layout'
import { Spinner, TableRow, TableCell, TableBody, theme, brandColors } from 'transfix-ui'
import { TestConnectionConfig } from '../TestConnectionConfig'
import { AS2CertificatesTable } from './AS2Certificates/AS2CertificatesTable'
import { useAs2ConnectionConfigCertificatesQuery } from './AS2Certificates/graphql/AS2ConnectionConfigCertificates'

interface IAS2ConnectionConfigDetailsProps {
  onSidebarClose: () => void
  isSidebarOpen: boolean
  displayUserFeedback(message: string, level: 'error' | 'info'): void
}

const useAS2ConnectionConfigDetailsStyles = makeStyles(() =>
  createStyles({
    titleText: {
      display: 'flex',
      flex: theme.spacing(1),
      alignItems: 'center',
    },
    secondary: {
      color: brandColors.coolGray5,
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
    },
  })
)

export const AS2ConnectionConfigDetails = memo<IAS2ConnectionConfigDetailsProps>(
  ({ displayUserFeedback, isSidebarOpen, onSidebarClose }) => {
    const { id: as2ConnectionConfigId } = useParams<{ id: string }>()
    const classes = useAS2ConnectionConfigDetailsStyles()

    const [as2ConnectionConfigDetails, setas2ConnectionConfigDetails] = useState<
      IAs2ConnectionConfig
    >()

    const { data, loading } = useAs2ConnectionConfigCertificatesQuery({
      variables: {
        id: String(as2ConnectionConfigId),
      },
      notifyOnNetworkStatusChange: true,
    })

    /*
     * When query updates, it sets new state of as2ConnectionConfig
     */
    useEffect(() => {
      setas2ConnectionConfigDetails(
        (data?.as2ConnectionConfig as IAs2ConnectionConfig) || undefined
      )
    }, [data?.as2ConnectionConfig])

    return (
      <Dialog
        id='as2-connection-config-side-panel'
        size='large'
        align='right'
        closeDialog={onSidebarClose}
        dialogOpen={isSidebarOpen}>
        {loading && <Spinner />}
        <Box mb={3}>
          <Box display='flex' justifyContent='center' alignItems='center' pl={2} pr={2}>
            <Typography variant='h2' className={classes.titleText}>
              <span data-test='as2-connection-config-id-header'>{as2ConnectionConfigId}</span>
              <span className={classes.secondary}>|</span>
              <span className='mr2' data-test='as2-connection-config-identifier-header'>
                {as2ConnectionConfigDetails?.identifier}
              </span>
            </Typography>
          </Box>
        </Box>
        <hr />
        <Table>
          <TableBody>
            <TableRow>
              <TableCell>Outbound URL</TableCell>
              <TableCell>{as2ConnectionConfigDetails?.outboundUrl!}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <hr />

        {as2ConnectionConfigDetails?.as2Certificates && (
          <AS2CertificatesTable
            configDetails={as2ConnectionConfigDetails}
            displayUserFeedback={displayUserFeedback}
          />
        )}

        <TestConnectionConfig
          configId={as2ConnectionConfigId}
          connectionType={IConnectionTypes.AS2}
          connectionConfigType={IConnectionConfigTypes.OutboundEdiMapperConnectionConfig}
        />
      </Dialog>
    )
  }
)
